import { railStepPosition, railTargetForStep, type RailStepKey } from "../rail-model";
import { workflowPillClass } from "../workflow-pill";
import { AvatarCard } from "./DocumentCards";
import { SalespagePhases } from "./OrderLinks";
import { DeliverableCard } from "./SalespageReview";
import { StrategyWaitingState } from "./StrategySection";
import {
  ActionError,
  FocusedSalesPage,
  MaterialFeedbackSummary,
  ReviewLoopIntro,
  focusedState,
  formatDate,
  resource,
  staffReturnedReason,
  type DeliverySectionContentProps
} from "./shared";

// F82: the stripe step is hidden and the publication lives inside the sales
// page step (phase 3), so the overview lists neither as a row of its own.
const SALES_OVERVIEW_KEYS = [
  "marketing_call",
  // 🔴 F152-W2 (audit drobnost 4): the profile step. It is listed HERE, in the
  // order Pavel gave it (second, between the video and the questionnaire), but
  // the filter below decides per client whether it belongs: in the full journey
  // the profile is chapter 1 and this list is chapter 2, so a plain
  // "is it in the rail" test would have added a row to fourteen-step clients
  // who never had it here.
  "profile",
  "q2",
  "strategy",
  "salespage",
  "emails",
  "webinar",
  "app_questionnaire",
  "handover",
  "release"
] as const;
type SalesOverviewKey = (typeof SALES_OVERVIEW_KEYS)[number];

function salesStepRailKey(key: SalesOverviewKey): RailStepKey {
  if (key === "release") return "app_release";
  return key;
}

export function SalesOverview(props: DeliverySectionContentProps) {
  // 🔴 N10: both of these used to be hand-written - the key list filtered on
  // one axis and the number a literal `includesApp ? 9 : 8`. That is a bug
  // waiting for a second axis, and F143 is it: a sales_only client's chapter 2
  // starts at step 1 and carries no Předání row. THIS client's rail is the one
  // answer to both questions.
  // F152-W2: the rail step itself, not just its key - the overview is chapter
  // 2, and since the profile step belongs to chapter 1 in one journey and to
  // chapter 2 in the other, membership is the CHAPTER the rail assigned this
  // client, read from the same derivation the rail's own headings use.
  const journey = new Map(props.railSteps.map((step) => [step.key, step]));
  const overviewKeys = SALES_OVERVIEW_KEYS
    .filter((key) => journey.get(salesStepRailKey(key))?.chapter === "sales");
  const firstKey = overviewKeys[0];
  const firstStepNumber = firstKey
    ? railStepPosition(props.railSteps, salesStepRailKey(firstKey))?.current ?? 1
    : 1;
  return (
    <section className="m5b-view sales-overview" aria-labelledby="sales-title">
      <header className="m5b-heading">
        <p className="eyebrow blue-eyebrow">{props.t("sales.overview.eyebrow")}</p>
        <h1 id="sales-title">{props.t("sales.overview.title")}</h1>
        <p className="lead">{props.t("sales.overview.copy")}</p>
      </header>
      <ol className="sales-overview-list" aria-label={props.t("sales.overview.listLabel")}>
        {overviewKeys.map((key, index) => {
          // F152-W2: the profile step has no section and no delivery row - its
          // state IS the rail's own reading of the confirmation and the dossier
          // (rail-model `stateByKey.profile`), so the row reads it there rather
          // than inventing a third answer.
          const state = key === "profile"
            ? journey.get("profile")?.state ?? null
            : key === "marketing_call" || key === "q2"
              ? props.sections.find((item) => item.key === key)?.state ?? null
              : props.dataErrors?.has("deliverables")
                ? null
                : props.salesSteps.find((item) => item.key === key)?.state ?? null;
          // K04: the row's destination is the RAIL's, not a second table that
          // knows only the delivery steps. That second table had no entry for
          // the two rows the client has already finished (marketing video,
          // sales questionnaire), so those two were the only DEAD rows on the
          // page while every locked row below them opened. One lookup, every
          // row leads somewhere.
          const target = railTargetForStep(salesStepRailKey(key));
          // A locked step has no client-visible event yet; its internal
          // updated_at is backstage state and printing it read as "your
          // locked step changed yesterday" (audit a1-r5-02).
          const changedAt = !state || state === "locked"
            ? null
            : salesOverviewChangedAt(key, props);
          return (
            <li data-sales-overview-step={key} key={key}>
              <button
                className={`sales-overview-state-${state ?? "unavailable"}`}
                type="button"
                data-target={target}
                onClick={() => props.onNavigate?.(target)}
              >
                <span className="sales-overview-number step-number-token" aria-hidden="true">
                  {String(firstStepNumber + index).padStart(2, "0")}
                </span>
                <span className="sales-overview-context">
                  <span className="sales-overview-name">{props.t(`rail.steps.${salesStepRailKey(key)}`)}</span>
                  <span>{props.t(`sales.overview.step.${key}`)}</span>
                  {/* K12: a row with no date prints nothing. The old
                      "Datum doplníme po prvním posunu" stood under every one
                      of the four locked rows - a system apology for a fact
                      the client cannot use, in the slot where finished rows
                      carry a real date. An empty slot says the same thing. */}
                  {changedAt && (
                    <time className="sales-overview-date" dateTime={changedAt}>
                      {props.t("sales.overview.updated").replace("{date}", formatDate(changedAt, props.locale))}
                    </time>
                  )}
                </span>
                <span className={`status-pill ${workflowPillClass(state)}`}>
                  {state ? props.t(`states.${state}`) : props.t("sales.overview.unavailable")}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {props.avatarMaterial && props.avatarMaterial.state !== "none" && (
        <div className="sales-overview-avatar">
          <AvatarCard material={props.avatarMaterial} t={props.t} />
        </div>
      )}
    </section>
  );
}

function salesOverviewChangedAt(
  key: SalesOverviewKey,
  props: DeliverySectionContentProps
): string | null {
  if (key === "marketing_call" || key === "q2" || key === "handover") {
    return props.sections.find((item) => item.key === key)?.changedAt ?? null;
  }
  // F152-W2: the profile step's own event is the confirmation, and the row
  // prints a date only for a step that is not locked - which for this step
  // means confirmed. Nothing else on this page knows when that happened, so the
  // row stays dateless rather than borrowing a section's clock (the audit a1-r5
  // law: a backstage timestamp printed as the client's own is a lie).
  if (key === "profile") return null;
  if (key === "strategy") {
    return props.deliverables
      .filter((item) => item.kind === "audit" || item.kind === "plan")
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0]?.updatedAt ?? null;
  }
  if (key === "salespage" || key === "emails" || key === "webinar") {
    return props.deliverables.find((item) => item.kind === key)?.updatedAt ?? null;
  }
  if (key === "app_questionnaire") {
    return resource(props.resources, "app_state")?.updatedAt
      ?? props.sections.find((item) => item.key === "app")?.changedAt
      ?? null;
  }
  return props.milestones.find((item) => item.key === "app_live")?.reachedAt
    ?? props.sections.find((item) => item.key === "app")?.changedAt
    ?? null;
}

// F82: one rail step, three phases - approval (the unchanged review flow),
// order links, publication. The phases render inside the step card and each
// states its own progress.
export function SalespagePage(props: DeliverySectionContentProps) {
  const item = props.deliverables.find((candidate) => candidate.kind === "salespage");
  const state = focusedState(props, "salespage");
  const reviewContent = (
    <>
      {item?.status === "review" && props.membershipRole === "owner" && (
        <>
          <ReviewLoopIntro material="salespage" placement="step" t={props.t} />
          <MaterialFeedbackSummary
            deliverableId={item.id}
            version={item.version}
            refreshVersion={props.feedbackRevision}
            onVersionAdvanced={props.onReloadData}
            t={props.t}
          />
        </>
      )}
      <div className="focused-sales-content">
        {item ? (
          <DeliverableCard
            kind="salespage"
            deliverable={item}
            canComment={props.membershipRole === "owner"}
            busyAction={props.busyAction}
            csrfToken={props.csrfToken}
            staffReturnedReason={staffReturnedReason(props.resources, "salespage")}
            t={props.t}
            onFeedback={(payload) => Promise.resolve(props.onGateFeedback?.(item.id, payload) ?? false)}
            canApprove
            onApprove={() => props.onGateApprove?.(item.id)}
          />
        ) : (
          <StrategyWaitingState
            material="salespage"
            profilePhotoUrl={props.profilePhotoUrl}
            titleKey="sales.preparingWait.salespage.title"
            copyKey="sales.preparingWait.salespage.copy"
            etaKey="sales.preparingWait.salespage.eta"
            t={props.t}
          />
        )}
      </div>
    </>
  );
  return (
    <FocusedSalesPage page="salespage" state={state} t={props.t}>
      <SalespagePhases
        deliverable={item}
        orderLinks={props.salespageDelivery?.orderLinks}
        delivery={props.salespageDelivery}
        isOwner={props.membershipRole === "owner"}
        csrfToken={props.csrfToken}
        partner={props.partner}
        profilePhotoUrl={props.profilePhotoUrl}
        locale={props.locale}
        t={props.t}
        onReloadData={props.onReloadData}
        reviewContent={reviewContent}
      />
      <ActionError t={props.t} issue={props.actionError} />
    </FocusedSalesPage>
  );
}
